import React from 'react';

const KimiModels = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Moonshot AI's <strong>Kimi</strong> has quietly become one of the most technically ambitious
      model series in the world. From K1.5's o1-level reasoning in January 2025 to K2.5's
      1-trillion-parameter Agent Swarm in January 2026 — coordinating up to
      <strong> 100 parallel AI sub-agents</strong> with 1,500 simultaneous tool calls —
      Kimi represents China's most sophisticated open-source frontier lab outside of Alibaba
      and DeepSeek.
    </p>

    <h2>Moonshot AI: The Company Behind Kimi</h2>
    <p>
      Founded in 2023 and headquartered in Beijing, Moonshot AI has pursued a distinctive
      strategy: build models with exceptional long-context and multimodal capabilities, release
      the weights openly, and charge for API access. By early 2026 the Kimi consumer app had
      tens of millions of active users in China, competing directly with Baidu's Ernie and
      ByteDance's Doubao.
    </p>

    <h2>The Kimi Model Timeline</h2>
    <div className="news-timeline">
      <div className="news-timeline-item">
        <span className="news-timeline-date">January 2025 — Kimi K1.5</span>
        <div>
          Moonshot AI's first frontier reasoning model — described as "o1-level multimodal
          reasoning." Built with advanced reinforcement learning and long chain-of-thought
          (CoT), using online mirror descent for training efficiency.
          <ul style={{marginTop: '8px', paddingLeft: '20px'}}>
            <li><strong>128K token</strong> context window</li>
            <li>MATH-500: <strong>96.2%</strong></li>
            <li>AIME: <strong>77.5%</strong></li>
            <li>Codeforces: <strong>94th percentile</strong></li>
            <li>MathVista: <strong>74.9%</strong></li>
          </ul>
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">July 2025 — Kimi K2</span>
        <div>
          Scaled to <strong>1 trillion total parameters</strong> via Mixture-of-Experts (MoE),
          with only 32B active per query. Maintained 128K context. Introduced agentic modes
          and tool-use capabilities. Released as open weights on HuggingFace.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">January 27, 2026 — Kimi K2.5</span>
        <div>
          Major evolution across every dimension: doubled context, native video understanding,
          Agent Swarm architecture, and open weights. Free access via Kimi.com.
        </div>
      </div>
    </div>

    <h2>Kimi K2.5 — Deep Dive</h2>

    <h3>Architecture</h3>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Total Parameters</span>
        <span className="news-spec-value">1 Trillion (MoE)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Active per Query</span>
        <span className="news-spec-value">32 Billion</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Context Window</span>
        <span className="news-spec-value">256,000 tokens (2× K2)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Vision Encoder</span>
        <span className="news-spec-value">400M MoonViT (native multimodal)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Modalities</span>
        <span className="news-spec-value">Text, Images, Video</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Licence</span>
        <span className="news-spec-value">Open weights on HuggingFace</span>
      </div>
    </div>

    <h3>Four Operational Modes</h3>
    <ul>
      <li><strong>Instant</strong>: Fast, single-pass response — everyday Q&A and lookup tasks</li>
      <li><strong>Thinking</strong>: Extended chain-of-thought reasoning — mathematics, code, complex analysis</li>
      <li><strong>Agent</strong>: Single autonomous agent with tool access — web search, code execution, file management</li>
      <li><strong>Agent Swarm</strong>: Up to 100 parallel sub-agents working simultaneously — the flagship capability</li>
    </ul>

    <h3>Agent Swarm — Parallel-Agent Reinforcement Learning (PARL)</h3>
    <p>
      K2.5's most technically distinctive feature is its <strong>Agent Swarm</strong> — a
      multi-agent orchestration system trained via <strong>Parallel-Agent Reinforcement
      Learning (PARL)</strong>. A lead agent decomposes complex tasks into parallel workstreams,
      dispatches up to <strong>100 sub-agents</strong> simultaneously, coordinates their outputs,
      and synthesises the final result.
    </p>
    <ul>
      <li>Up to <strong>1,500 parallel tool calls</strong> in a single task execution</li>
      <li><strong>4.5× faster</strong> execution versus sequential single-agent approaches</li>
      <li>Trained end-to-end with RL — the swarm coordination is learned, not hand-coded</li>
    </ul>

    <h3>Benchmark Performance</h3>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">HLE Reasoning</span>
        <span className="news-spec-value">50.2% (vs GPT-5.2 at 45.5%)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Image Text Extraction</span>
        <span className="news-spec-value">92.3% (vs GPT-5.2 at 80.7%)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">SWE-Bench (Code Fixing)</span>
        <span className="news-spec-value">76.8%</span>
      </div>
    </div>

    <h3>Pricing</h3>
    <p>
      K2.5 API pricing is <strong>$0.60 / million input tokens</strong> and
      <strong>$2.50–$3.00 / million output tokens</strong> — approximately 27–35% cheaper
      than GPT-5.2 for equivalent capability tasks. Free tier available at Kimi.com.
    </p>

    <h2>K2.5's Visual Coding Advantage</h2>
    <p>
      A standout use case: K2.5 can take a screenshot, UI design mockup, or video walkthrough
      and convert it directly into working frontend code. The MoonViT encoder processes visual
      structure natively — not as a description passed to a language model, but as a first-class
      input to the reasoning pipeline. This makes K2.5 particularly compelling for frontend
      development, design-to-code workflows, and UI automation agents.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        Kimi K2.5 represents the most complete open-source implementation of agentic AI to
        date: 1-trillion-parameter reasoning, native multimodal vision, 256K context, and
        a trained Agent Swarm capable of parallelising work across 100 sub-agents.
        The PARL training approach — teaching the swarm coordination through reinforcement
        learning rather than hard-coding it — is a meaningful architectural advance that
        other labs will need to match. At 27–35% below GPT-5.2 pricing with open weights,
        the economic and accessibility argument for Kimi is compelling.
      </p>
    </div>
  </article>
);

export default KimiModels;
